import type { DialogueContent } from '../../interactions/dialogue';
import type { InspectContent } from '../../interactions/inspect';
import type { SceneDefinition } from '../scene.types';

export const alimentationInspections: Record<string, InspectContent> = {
  'alimentation.sign': {
    title: 'Pancarte du rayon',
    imageUrl: '/props/prop-affiche.png',
    imageAlt: 'Pancarte manuscrite du rayon alimentation',
    lines: [
      'Ce produit était bien rangé il y a cinq minutes.',
      'Cette étagère est sous haute surveillance. Enfin, en théorie.',
      'Merci de ne pas goûter les sprats. Cela vaut aussi pour les humains.',
    ],
  },
};

export const alimentationDialogues: DialogueContent[] = [
  {
    id: 'alimentation.entry',
    lines: [
      'Le rayon était parfaitement rangé ce matin. Nous n’avons malheureusement aucun témoin.',
    ],
    character: 'La boutique',
    once: true,
  },
  {
    id: 'alimentation.scale',
    lines: [
      'La balance affiche « trop ». Elle ne précise jamais trop de quoi.',
      'Cette balance a été calibrée par un chien. Cela se voit.',
    ],
  },
  {
    id: 'alimentation.crate',
    lines: ['Le carton s’ouvre. Il était déjà ouvert. Nous n’en parlerons pas.'],
  },
  {
    id: 'alimentation.sign',
    lines: [
      'Pancarte : « Ce produit était bien rangé il y a cinq minutes. »',
      'Pancarte : « Cette étagère est sous haute surveillance. Enfin, en théorie. »',
    ],
  },
  {
    id: 'alimentation.falling',
    lines: [
      'Un paquet tombe de l’étagère. Personne ne l’a poussé. Officiellement.',
      'L’étagère se vide toute seule. C’est un phénomène connu ici.',
    ],
  },
  {
    id: 'alimentation.dog',
    lines: [
      'Un chien passe, attrape un produit et repart sans se retourner.',
      'Le rayon est provisoirement incomplet. Nous restons calmes.',
    ],
  },
];

export const alimentationScene: SceneDefinition = {
  id: 'alimentation',
  title: 'Le rayon Alimentation',
  background: '/scenes/scene-alimentation-background-v2.webp',
  entryDialogueId: 'alimentation.entry',
  layers: [],
  hotspots: [
    {
      id: 'alimentation.scale',
      label: 'Faire bouger la balance',
      action: 'animation',
      target: 'alimentation.scale',
      x: 41,
      y: 46,
      width: 10,
      height: 11,
      hint: 'La balance',
    },
    {
      id: 'alimentation.crate',
      label: 'Ouvrir le carton',
      action: 'animation',
      target: 'alimentation.crate',
      x: 49,
      y: 56,
      width: 11,
      height: 12,
      hint: 'Le carton',
    },
    {
      id: 'alimentation.sign',
      label: 'Lire la pancarte du rayon',
      action: 'inspect',
      target: 'alimentation.sign',
      x: 41,
      y: 8,
      width: 18,
      height: 14,
      hint: 'La pancarte',
    },
    {
      id: 'alimentation.bone',
      label: 'Regarder derrière les sacs empilés',
      action: 'collectible',
      target: 'bone-alimentation',
      x: 2,
      y: 74,
      width: 12,
      height: 14,
      hint: 'Les sacs',
    },
    {
      id: 'alimentation.falling',
      label: 'Faire tomber un paquet de l’étagère',
      action: 'animation',
      target: 'alimentation.falling',
      x: 2,
      y: 12,
      width: 12,
      height: 13,
      hint: 'Le paquet du haut',
    },
    {
      id: 'alimentation.dog',
      label: 'Suivre le chien qui rôde dans le rayon',
      action: 'animation',
      target: 'alimentation.dog',
      x: 66,
      y: 76,
      width: 14,
      height: 16,
      hint: 'Le chien',
      simplifiedLabel: 'Suivre le chien qui rôde dans le rayon',
    },
    {
      id: 'alimentation.product-1',
      label: 'Regarder le Pack Découverte',
      action: 'product',
      target: 'pack-debutant',
      x: 2,
      y: 28,
      width: 12,
      height: 13,
      hint: 'Le pack',
      simplifiedLabel: 'Voir le Pack Découverte',
    },
    {
      id: 'alimentation.product-2',
      label: 'Regarder le filet de poulet séché',
      action: 'product',
      target: 'filet-de-poulet',
      x: 2,
      y: 42,
      width: 12,
      height: 14,
      hint: 'Le filet de poulet',
      simplifiedLabel: 'Voir le filet de poulet séché',
    },
    {
      id: 'alimentation.product-3',
      label: 'Regarder le sachet de sprats',
      action: 'product',
      target: 'sprats-petits-poissons',
      x: 86,
      y: 28,
      width: 12,
      height: 13,
      hint: 'Les sprats',
      simplifiedLabel: 'Voir le sachet de sprats',
    },
    {
      id: 'alimentation.product-4',
      label: 'Regarder les ailes de poulet',
      action: 'product',
      target: 'ailes-de-poulet',
      x: 86,
      y: 42,
      width: 12,
      height: 14,
      hint: 'Les ailes de poulet',
      simplifiedLabel: 'Voir les ailes de poulet',
    },
    {
      id: 'alimentation.product-5',
      label: 'Regarder les cous de poulet',
      action: 'product',
      target: 'cous-de-poulet',
      x: 18,
      y: 32,
      width: 7,
      height: 11,
      hint: 'Les cous de poulet',
      simplifiedLabel: 'Voir les cous de poulet',
    },
    {
      id: 'alimentation.product-6',
      label: 'Regarder les lamelles de bœuf',
      action: 'product',
      target: 'lamelles-de-boeuf',
      x: 75,
      y: 32,
      width: 7,
      height: 11,
      hint: 'Les lamelles',
      simplifiedLabel: 'Voir les lamelles de bœuf',
    },
    {
      id: 'alimentation.product-7',
      label: 'Regarder les tripes d’agneau',
      action: 'product',
      target: 'tripe-dagneau',
      x: 27,
      y: 36,
      width: 6,
      height: 8,
      hint: 'Les tripes',
      simplifiedLabel: 'Voir les tripes d’agneau',
    },
    {
      id: 'alimentation.to-hall',
      label: 'Retourner au Hall',
      action: 'navigate',
      target: 'hall',
      x: 2,
      y: 88,
      width: 12,
      height: 10,
      hint: 'Retour au Hall',
      simplifiedLabel: 'Retourner au Hall',
    },
    {
      id: 'alimentation.to-comptoir',
      label: 'Aller directement au Comptoir',
      action: 'navigate',
      target: 'comptoir',
      x: 43,
      y: 38,
      width: 14,
      height: 12,
      hint: 'Le Comptoir',
      simplifiedLabel: 'Aller au Comptoir',
    },
  ],
};
